import React from 'react';
import styled from 'styled-components';
import { formatYearDisplay } from '../../../utils/strings';

const assignedBadgeStyles = (size) => `
  background: #F9F3FF;
  color: #50009D;
  padding: ${size === 'small' ? '4px 12px' : '6px 14px'};
  font-size: ${size === 'small' ? '12px' : '14px'};
`;
const unassignedBadgeStyles = (size) => `
  background: #FFF4EA;
  color: #873800;
  padding: ${size === 'small' ? '4px 12px' : '6px 14px'};
  font-size: ${size === 'small' ? '12px' : '14px'};
`;

export const SubjectBadge = styled.div`
  display: inline-flex;
  align-items: center;
  font-weight: 400;
  border-radius: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
  white-space: nowrap;
  ${(props) => (props.$unassigned ? unassignedBadgeStyles(props.size || 'small') : assignedBadgeStyles(props.size || 'small'))}
`;

export const YearBadge = styled.div`
  display: inline-flex;
  align-items: center;
  font-weight: 400;
  border-radius: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
  white-space: nowrap;
  ${(props) => (props.$unassigned ? unassignedBadgeStyles(props.size || 'small') : assignedBadgeStyles(props.size || 'small'))}
`;

export const TaskTypeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  font-weight: 400;
  border-radius: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
  white-space: nowrap;
  ${(props) => assignedBadgeStyles(props.size || 'small')}
`;

export const MoreBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: ${(props) => (props.size === 'small' ? '4px 10px' : '6px 12px')};
  background-color: #F9F3FF;
  color: #50009D;
  font-size: ${(props) => (props.size === 'small' ? '12px' : '14px')};
  font-weight: 500;
  border-radius: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
  white-space: nowrap;
`;

export const ResponseTypeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  font-weight: 400;
  border-radius: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
  white-space: nowrap;
  ${(props) => assignedBadgeStyles(props.size || 'small')}
`;

export const BadgesContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.gap || '6px')};
  flex-wrap: wrap;
`;

export const MultiSubjectBadges = ({
  years = [],
  subjects = [],
  taskType,
  taskTypes,
  responseType,
  responseTypes,
  maxVisibleSubjects = 3,
  maxVisibleYears = 3,
  size = 'small',
  className,
}) => {
  const resolvedTaskTypes = Array.isArray(taskTypes) ? taskTypes : (taskType != null ? [taskType] : []);
  const resolvedResponseTypes = Array.isArray(responseTypes) ? responseTypes : (responseType != null ? [responseType] : []);
  const toTitleCase = (s) => (s == null ? '' : String(s).split(/[\s_]+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '));
  const toDisplay = (v) => (typeof v === 'object' && v != null && 'label' in v ? v.label : v);

  const maxVisibleTaskTypes = 2;
  const maxVisibleResponseTypes = 2;

  const hasContent = years.length > 0 || subjects.length > 0 || resolvedTaskTypes.length > 0 || resolvedResponseTypes.length > 0;

  if (!hasContent) return null;

  const formatGroupedLabel = (items, maxVisible, format = (v) => v) => {
    if (items.length === 0) return null;
    const visible = items.slice(0, maxVisible);
    const remaining = items.length - maxVisible;
    const value = visible.map(format).join(', ');
    const suffix = remaining > 0 ? ` +${remaining} more` : '';
    return value + suffix;
  };

  const yearsLabel = formatGroupedLabel(years, maxVisibleYears, formatYearDisplay);
  const subjectsLabel = formatGroupedLabel(subjects, maxVisibleSubjects);
  const taskTypesLabel = formatGroupedLabel(resolvedTaskTypes, maxVisibleTaskTypes, (v) => toTitleCase(toDisplay(v)));
  const responseTypesLabel = formatGroupedLabel(resolvedResponseTypes, maxVisibleResponseTypes, (v) => toTitleCase(toDisplay(v)));
  const isYearsUnassigned = years.length === 1 && String(years[0]).toLowerCase() === 'unassigned';
  const isSubjectsUnassigned = subjects.length === 1 && String(subjects[0]).toLowerCase() === 'unassigned';

  return (
    <BadgesContainer className={className}>
      {yearsLabel && (
        <YearBadge size={size} $unassigned={isYearsUnassigned}>
          {yearsLabel}
        </YearBadge>
      )}
      {subjectsLabel && (
        <SubjectBadge size={size} $unassigned={isSubjectsUnassigned}>
          {subjectsLabel}
        </SubjectBadge>
      )}
      {taskTypesLabel && (
        <TaskTypeBadge size={size}>
          {taskTypesLabel}
        </TaskTypeBadge>
      )}
      {responseTypesLabel && (
        <ResponseTypeBadge size={size}>
          {responseTypesLabel}
        </ResponseTypeBadge>
      )}
    </BadgesContainer>
  );
};

export const YearChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1.5px solid ${(props) => (props.selected ? '#E1BEE7' : '#e5e7eb')};
  background-color: ${(props) => (props.selected ? '#F3E5F5' : '#ffffff')};
  color: ${(props) => (props.selected ? '#7B1FA2' : '#4b464f')};

  &:hover {
    border-color: ${(props) => (props.selected ? '#E1BEE7' : '#E1BEE7')};
    background-color: ${(props) => (props.selected ? '#F3E5F5' : '#FCE4EC')};
  }

  &:active {
    background-color: ${(props) => (props.selected ? '#E1BEE7' : '#F3E5F5')};
  }
`;

export const SubjectChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1.5px solid ${(props) => (props.selected ? '#E1BEE7' : '#e5e7eb')};
  background-color: ${(props) => (props.selected ? '#F3E5F5' : '#ffffff')};
  color: ${(props) => (props.selected ? '#7B1FA2' : '#4b464f')};

  &:hover {
    border-color: ${(props) => (props.selected ? '#E1BEE7' : '#E1BEE7')};
    background-color: ${(props) => (props.selected ? '#F3E5F5' : '#FCE4EC')};
  }

  &:active {
    background-color: ${(props) => (props.selected ? '#E1BEE7' : '#F3E5F5')};
  }
`;

export const ChipRemove = styled.span`
  font-size: 16px;
  line-height: 1;
  margin-left: 4px;
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

export const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const TaskTypeChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1.5px solid ${(props) => (props.selected ? '#E1BEE7' : '#e5e7eb')};
  background-color: ${(props) => (props.selected ? '#F3E5F5' : '#ffffff')};
  color: ${(props) => (props.selected ? '#7B1FA2' : '#4b464f')};

  &:hover {
    border-color: ${(props) => (props.selected ? '#E1BEE7' : '#E1BEE7')};
    background-color: ${(props) => (props.selected ? '#F3E5F5' : '#FCE4EC')};
  }

  &:active {
    background-color: ${(props) => (props.selected ? '#E1BEE7' : '#F3E5F5')};
  }
`;

export const ResponseTypeChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 8px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1.5px solid ${(props) => (props.selected ? '#E1BEE7' : '#e5e7eb')};
  background-color: ${(props) => (props.selected ? '#F3E5F5' : '#ffffff')};
  color: ${(props) => (props.selected ? '#7B1FA2' : '#4b464f')};

  &:hover {
    border-color: ${(props) => (props.selected ? '#E1BEE7' : '#E1BEE7')};
    background-color: ${(props) => (props.selected ? '#F3E5F5' : '#FCE4EC')};
  }

  &:active {
    background-color: ${(props) => (props.selected ? '#E1BEE7' : '#F3E5F5')};
  }
`;
